import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Avatar, Chip, CircularProgress, Alert, IconButton, Breadcrumbs, Link,
    Button, Dialog, DialogTitle, DialogContent, Grid
} from '@mui/material';
import { ArrowBack, RestaurantMenu, Inventory, Close, FiberManualRecord } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { getVendorMenuItems } from '../../services/Api';

const GetMenuItems = () => {
    const { vendorId } = useParams();
    const navigate = useNavigate();
    const [menuItems, setMenuItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal state
    const [selectedItem, setSelectedItem] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);

    const fetchMenuItems = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getVendorMenuItems(vendorId);
            if (response.success || response.data) {
                const src = response.data || response;
                let targetArray = [];

                if (Array.isArray(src)) targetArray = src;
                else if (src && typeof src === 'object') {
                    if (Array.isArray(src.menuItems)) targetArray = src.menuItems;
                    else if (Array.isArray(src.data)) targetArray = src.data;
                }

                setMenuItems(targetArray);
            } else {
                setError(response.message || 'Failed to fetch menu items');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred while fetching menu items');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMenuItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vendorId]);

    const handleViewDetails = (item) => {
        setSelectedItem(item);
        setDetailsOpen(true);
    };

    const handleCloseDetails = () => {
        setDetailsOpen(false);
        setSelectedItem(null);
    };

    return (
        <Box>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                    <Link underline="hover" color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate('/vendors/food'); }}>
                        Food Vendors
                    </Link>
                    <Typography color="text.primary">Menu Items</Typography>
                </Breadcrumbs>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => navigate('/vendors/food')} sx={{ border: '1px solid', borderColor: 'divider' }}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        Menu Items
                    </Typography>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                                <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Price Range</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 10 }}>
                                        <CircularProgress size={40} />
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                            Loading menu items...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : menuItems.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 10 }}>
                                        <RestaurantMenu sx={{ fontSize: 48, color: 'text.secondary', mb: 1, opacity: 0.5 }} />
                                        <Typography variant="body1" color="text.secondary">
                                            No menu items found for this vendor.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                menuItems.map((item) => {
                                    const basePrice = item.variants?.[0]?.price || 'N/A';
                                    const isNonVeg = item.dishType?.toLowerCase().includes('non');

                                    return (
                                        <TableRow key={item.id} hover>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Avatar
                                                        variant="rounded"
                                                        src={item.image}
                                                        sx={{ width: 48, height: 48, backgroundColor: 'rgba(0,0,0,0.05)' }}
                                                    >
                                                        <Inventory sx={{ fontSize: 24, color: 'text.secondary' }} />
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                            {item.dishName}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            ID: {item.id}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <FiberManualRecord sx={{ fontSize: 14, color: isNonVeg ? '#e53935' : '#43a047' }} />
                                                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                                        {item.dishType}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    ₹{basePrice}
                                                    {item.variants?.length > 1 && '+'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={item.isAvailable ? "Available" : "Unavailable"}
                                                    size="small"
                                                    color={item.isAvailable ? "success" : "default"}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => handleViewDetails(item)}
                                                >
                                                    View Details
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Item Details Modal */}
            <Dialog
                open={detailsOpen}
                onClose={handleCloseDetails}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>Menu Item Portfolio</Typography>
                    <IconButton onClick={handleCloseDetails} sx={{ color: 'text.secondary' }}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 4 }}>
                    {selectedItem && (
                        <Grid container spacing={3}>
                            {/* Image Section */}
                            <Grid size={{ xs: 12, md: 3.5 }}>
                                <Box
                                    component="img"
                                    src={selectedItem.image}
                                    alt={selectedItem.dishName}
                                    sx={{
                                        width: '100%',
                                        aspectRatio: '1/1',
                                        objectFit: 'cover',
                                        borderRadius: 3,
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                        border: '1px solid',
                                        borderColor: 'divider'
                                    }}
                                />
                            </Grid>

                            {/* Content Section */}
                            <Grid size={{ xs: 12, md: 8.5 }}>
                                <Box sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 1 }}>
                                        <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary' }}>
                                            {selectedItem.dishName}
                                        </Typography>
                                        <Chip
                                            icon={<FiberManualRecord sx={{ fontSize: '10px !important' }} />}
                                            label={selectedItem.dishType?.toUpperCase()}
                                            size="small"
                                            sx={{
                                                fontWeight: 700,
                                                borderRadius: 1,
                                                bgcolor: selectedItem.dishType?.toLowerCase().includes('non') ? 'rgba(229, 57, 53, 0.08)' : 'rgba(67, 160, 71, 0.08)',
                                                color: selectedItem.dishType?.toLowerCase().includes('non') ? '#e53935' : '#43a047',
                                                border: '1px solid',
                                                borderColor: 'currentColor'
                                            }}
                                        />
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>description :&nbsp;
                                        {selectedItem.description || 'Discover our signature dish, prepared with the finest ingredients and authentic recipes.'}
                                    </Typography>
                                </Box>

                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <RestaurantMenu color="primary" sx={{ fontSize: 16 }} />
                                    Variants & Pricing
                                </Typography>

                                <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                                    <Table size="small">
                                        <TableHead sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700, py: 1, px: 1 }}>Variant</TableCell>
                                                <TableCell sx={{ fontWeight: 700, px: 1 }}>Original</TableCell>
                                                <TableCell sx={{ fontWeight: 700, px: 1 }}>Offer</TableCell>
                                                <TableCell sx={{ fontWeight: 700, px: 1 }}>Price</TableCell>
                                                <TableCell sx={{ fontWeight: 700, px: 1 }} align="center">Qty</TableCell>
                                                <TableCell sx={{ fontWeight: 700, px: 1 }} align="center">Serves</TableCell>
                                                <TableCell sx={{ fontWeight: 700, px: 1 }} align="right">Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {selectedItem.variants?.map((v) => (
                                                <TableRow key={v.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                                                    <TableCell sx={{ fontWeight: 600, color: 'text.primary', px: 1 }}>{v.name}</TableCell>
                                                    <TableCell sx={{ textDecoration: 'line-through', color: 'text.disabled', fontSize: '0.75rem', px: 1 }}>₹{v.price}</TableCell>
                                                    <TableCell sx={{ px: 1 }}>
                                                        <Chip
                                                            label={`${Math.round(v.discountPercentage)}% OFF`}
                                                            size="small"
                                                            sx={{
                                                                fontWeight: 800,
                                                                height: 18,
                                                                fontSize: '0.6rem',
                                                                bgcolor: '#fff5f5',
                                                                color: '#e53935',
                                                                border: '1px solid #ffcdd2'
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ color: 'primary.main', fontWeight: 800, fontSize: '0.9rem', px: 1 }}>₹{v.discountPrice}</TableCell>
                                                    <TableCell align="center" sx={{ px: 1 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>{v.quantity}</Typography>
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ px: 1 }}>
                                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>{v.serves} Pers.</Typography>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ px: 1 }}>
                                                        <Chip
                                                            label={v.isAvailable ? "Available" : "Unavailable"}
                                                            size="small"
                                                            color={v.isAvailable ? "success" : "default"}
                                                            variant={v.isAvailable ? "filled" : "outlined"}
                                                            sx={{ height: 18, fontWeight: 700, fontSize: '0.6rem', textTransform: 'uppercase' }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default GetMenuItems;
